import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend, Line, LineChart } from "recharts";
import { Lock, RefreshCw, DollarSign, Users, Repeat, TrendingUp, Percent, Sparkles, Download, KeyRound, Upload, Mail, Bell } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ppe_admin_token";

const PRODUCT_COLORS = {
  pro_lifetime: "#FFD400",
  pro_monthly: "#00F0FF",
  season_daily: "#F0ABFC",
  season_monthly: "#A78BFA",
  season_30day: "#FF9020",
};

function fmtUSD(cents) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AdminRevenue() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [rangeDays, setRangeDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txData, setTxData] = useState(null);
  const [refEmail, setRefEmail] = useState("");
  const [refBusy, setRefBusy] = useState(false);
  const [refResult, setRefResult] = useState(null);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkPrefix, setBulkPrefix] = useState("VIP");
  const [bulkPercent, setBulkPercent] = useState(20);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [couponAnalytics, setCouponAnalytics] = useState(null);
  const [pushAnalytics, setPushAnalytics] = useState(null);
  const [pushCopy, setPushCopy] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [couponCopy, setCouponCopy] = useState(null);
  const [couponCopyBusy, setCouponCopyBusy] = useState(false);

  const load = async (tkn = token, days = rangeDays) => {
    if (!tkn) return;
    setLoading(true);
    setAuthError(false);
    try {
      const res = await axios.post(`${API}/admin/revenue`, { token: tkn, range_days: days });
      setData(res.data);
      loadTx(tkn, 1);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthError(true);
        setToken("");
        localStorage.removeItem(TOKEN_KEY);
        toast.error("Invalid admin token");
      } else {
        toast.error("Failed to load revenue");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTx = async (tkn, page) => {
    try {
      const res = await axios.post(`${API}/admin/transactions`, { token: tkn, page, per_page: 20 });
      setTxData(res.data);
      setTxPage(page);
    } catch {}
    try {
      const res = await axios.post(`${API}/admin/coupons/analytics`, { token: tkn });
      setCouponAnalytics(res.data || []);
    } catch {}
    try {
      const res = await axios.post(`${API}/admin/push-analytics`, { token: tkn, days: 14 });
      setPushAnalytics(res.data);
    } catch {}
    try {
      const res = await axios.post(`${API}/admin/push-copy/list`, { token: tkn });
      setPushCopy(res.data?.segments || []);
    } catch {}
    try {
      const res = await axios.post(`${API}/admin/coupon-copy/list`, { token: tkn });
      setCouponCopy(res.data?.codes || []);
    } catch {}
  };

  const savePushCopy = async (segment, variants) => {
    setCopyBusy(true);
    try {
      await axios.post(`${API}/admin/push-copy/save`, { token, segment, variants });
      toast.success(`SAVED · ${segment}`);
      const res = await axios.post(`${API}/admin/push-copy/list`, { token });
      setPushCopy(res.data?.segments || []);
    } catch (e) {
      toast.error("Failed to save push copy");
    } finally {
      setCopyBusy(false);
    }
  };

  const saveCouponCopy = async (code, variants) => {
    setCouponCopyBusy(true);
    try {
      await axios.post(`${API}/admin/coupon-copy/save`, { token, code, variants });
      toast.success(`SAVED · ${code}`);
      const res = await axios.post(`${API}/admin/coupon-copy/list`, { token });
      setCouponCopy(res.data?.codes || []);
    } catch (e) {
      toast.error("Failed to save coupon copy");
    } finally {
      setCouponCopyBusy(false);
    }
  };

  const createReferral = async () => {
    const e = refEmail.trim().toLowerCase();
    if (!e.includes("@")) return toast.error("Enter a valid email");
    setRefBusy(true);
    try {
      const res = await axios.post(`${API}/referral/create`, { email: e, percent_off: 15 });
      setRefResult(res.data);
      toast.success(res.data.already_existed ? "Existing referral code returned" : "New referral created");
    } catch (err) {
      toast.error("Could not create referral");
    } finally {
      setRefBusy(false);
    }
  };

  const issueBulk = async () => {
    // Accept both CSV (single column of emails) and comma/whitespace lists
    const emails = Array.from(new Set(
      bulkCsv.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter((s) => s.includes("@"))
    ));
    if (emails.length === 0) return toast.error("Paste at least one email");
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/coupons/bulk-issue`, {
        token, prefix: bulkPrefix.toUpperCase(), percent_off: Number(bulkPercent) || 20, emails,
      });
      setBulkResult(res.data);
      toast.success(`Issued ${res.data.created} coupon(s)`);
    } catch (e) {
      toast.error("Bulk issue failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadBulkCsv = () => {
    if (!bulkResult) return;
    const rows = [["email", "coupon_code", "percent_off"]];
    for (const r of bulkResult.results) rows.push([r.email, r.code || "", r.percent_off || ""]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `ppe-bulk-coupons-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (token) load(token, rangeDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, rangeDays]);

  // ---- Token gate ----
  if (!token) {
    return (
      <div className="min-h-screen w-full grid-bg flex items-center justify-center" data-testid="admin-revenue-gate">
        <div className="border border-white/10 bg-black/70 backdrop-blur-xl p-8 w-full max-w-md">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#FFD400] mb-3">
            <Lock size={14} /> ADMIN · REVENUE
          </div>
          <div className="font-display text-3xl tracking-tighter text-white">Passcode</div>
          <p className="text-xs text-white/50 mt-2 tracking-wider">
            Enter your <span className="text-white/80">ADMIN_TOKEN</span> to unlock the revenue dashboard.
          </p>
          <input
            data-testid="admin-token-input"
            type="password"
            placeholder="paste admin token"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && tokenInput && (localStorage.setItem(TOKEN_KEY, tokenInput.trim()), setToken(tokenInput.trim()))}
            className="mt-6 w-full bg-transparent border-0 border-b border-white/20 rounded-none focus:outline-none focus:border-[#FFD400] font-mono text-white py-2 px-1"
          />
          <button
            data-testid="admin-token-submit"
            disabled={!tokenInput}
            onClick={() => {
              const t = tokenInput.trim();
              if (!t) return;
              localStorage.setItem(TOKEN_KEY, t);
              setToken(t);
            }}
            className="mt-6 w-full px-5 py-3 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black transition-colors disabled:opacity-40 text-xs tracking-[0.3em]"
          >
            UNLOCK →
          </button>
          {authError && <div className="mt-3 text-xs text-[#FF0055] tracking-wider">Invalid token · try again</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full grid-bg" data-testid="admin-revenue">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#FFD400]">
              <DollarSign size={12} /> ADMIN · REVENUE
            </div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-white mt-1">
              Live <span className="text-[#FFD400]" style={{ textShadow: "0 0 22px rgba(255,212,0,0.5)" }}>Cashflow</span>
            </h1>
            <p className="text-xs text-white/40 tracking-wider mt-1">
              Generated {data ? new Date(data.generated_at).toLocaleString() : "—"}
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="range-toggle">
            {[7, 30, 90, 0].map((d) => (
              <button
                key={d}
                data-testid={`range-${d}`}
                onClick={() => setRangeDays(d)}
                className="px-3 py-1.5 border text-[10px] tracking-[0.3em] transition-colors"
                style={{
                  borderColor: rangeDays === d ? "#FFD400" : "rgba(255,255,255,0.15)",
                  color: rangeDays === d ? "#FFD400" : "rgba(255,255,255,0.55)",
                  boxShadow: rangeDays === d ? "0 0 12px rgba(255,212,0,0.35)" : "none",
                }}
              >
                {d === 0 ? "ALL" : `${d}D`}
              </button>
            ))}
            <button
              data-testid="admin-refresh"
              onClick={() => load()}
              disabled={loading}
              className="ml-2 px-3 py-1.5 border border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors disabled:opacity-40 text-[10px] tracking-[0.3em] flex items-center gap-2"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> REFRESH
            </button>
            <button
              data-testid="admin-download-csv"
              onClick={() => {
                if (!token) return;
                const url = `${API}/admin/revenue.csv?token=${encodeURIComponent(token)}`;
                window.location.href = url;
              }}
              className="px-3 py-1.5 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors text-[10px] tracking-[0.3em] flex items-center gap-2"
            >
              <Download size={12} /> CSV
            </button>
            <button
              data-testid="admin-rotate-token"
              onClick={async () => {
                if (!window.confirm("Rotate admin token? Any other devices logged in will be locked out immediately.")) return;
                try {
                  const res = await axios.post(`${API}/admin/rotate-token`, { token });
                  const nt = res.data?.new_token;
                  if (nt) {
                    localStorage.setItem(TOKEN_KEY, nt);
                    setToken(nt);
                    toast.success("Admin token rotated · this device stays logged in.");
                    // Show the new token in a modal-ish alert so admin can save it
                    setTimeout(() => window.prompt("Copy your new admin token (also stored on this device):", nt), 100);
                  }
                } catch (e) {
                  toast.error("Rotation failed");
                }
              }}
              className="px-3 py-1.5 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors text-[10px] tracking-[0.3em] flex items-center gap-2"
            >
              <KeyRound size={12} /> ROTATE
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setData(null); }}
              className="px-3 py-1.5 border border-white/20 text-white/60 hover:border-[#FF0055] hover:text-[#FF0055] text-[10px] tracking-[0.3em]"
            >
              LOCK
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="kpi-grid">
          <Kpi icon={<DollarSign size={14} />} label="TOTAL · ALL TIME" value={data ? fmtUSD(data.kpis.total_all_cents) : "—"} accent="#FFD400" testid="kpi-total-all" />
          <Kpi icon={<TrendingUp size={14} />} label="LAST 30 DAYS" value={data ? fmtUSD(data.kpis.total_30d_cents) : "—"} accent="#39FF14" testid="kpi-total-30d" />
          <Kpi icon={<Repeat size={14} />} label="MRR" value={data ? fmtUSD(data.kpis.mrr_cents) : "—"} accent="#00F0FF" testid="kpi-mrr" />
          <Kpi icon={<Users size={14} />} label="ACTIVE SUBS" value={data ? data.kpis.active_subs : "—"} sub={data ? `${data.kpis.active_pro_monthly} Pro · ${data.kpis.active_season_monthly} Season` : ""} accent="#A78BFA" testid="kpi-active-subs" />
          <Kpi icon={<Sparkles size={14} />} label="ACTIVE ONE-TIME PASSES" value={data ? data.kpis.active_season_onetime : "—"} accent="#F0ABFC" testid="kpi-onetime" />
          <Kpi icon={<Percent size={14} />} label="FREE → PAID" value={data ? `${data.kpis.conversion_pct}%` : "—"} sub={data ? `${data.kpis.unique_paid_emails} of ${data.kpis.unique_daily_players}` : ""} accent="#FF9020" testid="kpi-conv" />
        </div>

        {/* Daily revenue chart */}
        <div className="border border-white/10 bg-black/40 backdrop-blur-md p-6 mb-8" data-testid="revenue-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.4em] text-white/50">DAILY REVENUE · BY PRODUCT</div>
              <div className="text-xs text-white/40 tracking-wider mt-1">Last {data?.range_days || 30} days · gross in USD</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={(data?.series || []).map((r) => ({
                date: r.date.slice(5),
                pro_lifetime: r.pro_lifetime / 100,
                pro_monthly: r.pro_monthly / 100,
                season_daily: r.season_daily / 100,
                season_monthly: r.season_monthly / 100,
                season_30day: r.season_30day / 100,
              }))} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.5)" fontSize={10} tickLine={false} />
                <YAxis stroke="rgba(255,255,255,0.5)" fontSize={10} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ background: "rgba(0,0,0,0.9)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontFamily: "monospace", fontSize: 11 }}
                  formatter={(v) => `$${Number(v).toFixed(2)}`}
                />
                <Legend wrapperStyle={{ fontSize: 10, letterSpacing: "0.2em" }} />
                <Bar dataKey="pro_lifetime" stackId="a" fill={PRODUCT_COLORS.pro_lifetime} name="PRO · LIFETIME" />
                <Bar dataKey="pro_monthly" stackId="a" fill={PRODUCT_COLORS.pro_monthly} name="PRO · MONTHLY" />
                <Bar dataKey="season_daily" stackId="a" fill={PRODUCT_COLORS.season_daily} name="SEASON · 24H" />
                <Bar dataKey="season_monthly" stackId="a" fill={PRODUCT_COLORS.season_monthly} name="SEASON · MONTHLY" />
                <Bar dataKey="season_30day" stackId="a" fill={PRODUCT_COLORS.season_30day} name="SEASON · 30-DAY" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {data && data.kpis.total_all_cents === 0 && (
            <div className="mt-4 text-[10px] tracking-[0.3em] text-white/40 text-center">
              No paid transactions yet — chart will populate once your first checkout completes.
            </div>
          )}
        </div>

        {/* Per-product table */}
        <div className="border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="product-table-card">
          <div className="text-[10px] tracking-[0.4em] text-white/50 mb-4">PER-PRODUCT · UNITS · REVENUE · MRR</div>
          <table className="w-full text-xs" data-testid="product-table">
            <thead>
              <tr className="text-[10px] tracking-[0.3em] text-white/40 border-b border-white/10">
                <th className="text-left py-2">PRODUCT</th>
                <th className="text-right">UNIT</th>
                <th className="text-right">TYPE</th>
                <th className="text-right">UNITS SOLD</th>
                <th className="text-right">GROSS</th>
                <th className="text-right">MRR</th>
              </tr>
            </thead>
            <tbody className="font-mono text-white/85">
              {(data?.products || []).map((p) => (
                <tr key={p.lookup_key} className="border-b border-white/5" data-testid={`row-${p.lookup_key}`}>
                  <td className="py-3">
                    <span className="inline-block w-2 h-2 mr-2 align-middle" style={{ background: PRODUCT_COLORS[p.lookup_key] }} />
                    <span className="text-white">{p.label}</span>
                    <span className="ml-2 text-white/40 text-[10px] tracking-widest">{p.lookup_key}</span>
                  </td>
                  <td className="text-right text-white/70">{fmtUSD(p.unit_price_cents)}</td>
                  <td className="text-right text-white/70">{p.recurring ? "RECURRING" : "ONE-TIME"}</td>
                  <td className="text-right tabular-nums">{p.units_sold}</td>
                  <td className="text-right tabular-nums text-[#39FF14]">{fmtUSD(p.gross_cents)}</td>
                  <td className="text-right tabular-nums text-cyan-300">{p.recurring ? fmtUSD(p.mrr_cents || 0) : "—"}</td>
                </tr>
              ))}
              {data && (
                <tr className="text-[11px] font-semibold" data-testid="row-totals">
                  <td className="py-3 text-white tracking-[0.3em]">TOTAL</td>
                  <td></td>
                  <td></td>
                  <td className="text-right tabular-nums text-white">
                    {data.products.reduce((s, p) => s + p.units_sold, 0)}
                  </td>
                  <td className="text-right tabular-nums text-[#39FF14]">{fmtUSD(data.kpis.total_all_cents)}</td>
                  <td className="text-right tabular-nums text-cyan-300">{fmtUSD(data.kpis.mrr_cents)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Coupon analytics */}
        <div className="mt-6 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="coupon-analytics-card">
          <div className="text-[10px] tracking-[0.4em] text-[#F0ABFC] mb-4 flex items-center gap-2">
            <Percent size={12} /> COUPON PERFORMANCE
          </div>
          {couponAnalytics && couponAnalytics.length > 0 ? (
            <table className="w-full text-xs" data-testid="coupon-analytics-table">
              <thead>
                <tr className="text-[10px] tracking-[0.3em] text-white/40 border-b border-white/10">
                  <th className="text-left py-2">CODE</th>
                  <th className="text-right">REDEMPTIONS</th>
                  <th className="text-right">GROSS DRIVEN</th>
                  <th className="text-right">LAST USED</th>
                </tr>
              </thead>
              <tbody className="font-mono text-white/80">
                {couponAnalytics.map((c) => (
                  <tr key={c.code} className="border-b border-white/5" data-testid={`coupon-row-${c.code}`}>
                    <td className="py-2 text-[#F0ABFC]">{c.code}</td>
                    <td className="text-right tabular-nums">{c.redemptions}</td>
                    <td className="text-right tabular-nums text-[#39FF14]">{fmtUSD(c.gross_cents)}</td>
                    <td className="text-right text-white/60">{c.last_used_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-4">NO COUPON REDEMPTIONS YET</div>
          )}
        </div>

        {/* Push segment analytics */}
        <div className="mt-6 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="push-analytics-card">
          <div className="text-[10px] tracking-[0.4em] text-[#00F0FF] mb-4 flex items-center gap-2">
            <Bell size={12} /> PERSONALIZED PUSH · 14D
          </div>
          {pushAnalytics && pushAnalytics.rows && pushAnalytics.rows.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="push-analytics-totals">
                {pushAnalytics.segments.map((seg) => {
                  const t = pushAnalytics.totals[seg] || { sent: 0, opened: 0, rate: 0 };
                  const segColor = {
                    active: "#00F0FF",
                    resume_run: "#FFD400",
                    streak_risk: "#FF9020",
                    churned: "#FF0055",
                    unknown: "#888",
                  }[seg] || "#888";
                  return (
                    <div key={seg} className="border border-white/10 p-3" style={{ borderColor: `${segColor}55` }}>
                      <div className="text-[9px] tracking-[0.3em] text-white/50 mb-1">{seg.toUpperCase().replace("_", " ")}</div>
                      <div className="text-xl font-bold tabular-nums" style={{ color: segColor }}>{t.rate}%</div>
                      <div className="text-[10px] text-white/40">{t.opened} / {t.sent} opens</div>
                    </div>
                  );
                })}
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={pushAnalytics.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="day" stroke="rgba(255,255,255,0.35)" style={{ fontSize: 10 }} />
                  <YAxis stroke="rgba(255,255,255,0.35)" style={{ fontSize: 10 }} unit="%" />
                  <Tooltip contentStyle={{ background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.15)", fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {pushAnalytics.segments.map((seg) => {
                    const color = {
                      active: "#00F0FF",
                      resume_run: "#FFD400",
                      streak_risk: "#FF9020",
                      churned: "#FF0055",
                      unknown: "#888",
                    }[seg] || "#888";
                    return (
                      <Line
                        key={seg}
                        type="monotone"
                        dataKey={`${seg}_rate`}
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        name={seg}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-8">NO PUSH DATA YET · sends and opens will populate here after the next daily cron</div>
          )}
        </div>

        {/* Push copy A/B editor */}
        <div className="mt-6 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="push-copy-editor">
          <div className="text-[10px] tracking-[0.4em] text-[#F0ABFC] mb-4 flex items-center gap-2">
            <Bell size={12} /> PUSH COPY · A/B VARIANTS
          </div>
          <p className="text-xs text-white/50 tracking-wider mb-4">
            Draft two variants per segment. The daily cron picks randomly per send; open-rate per variant is displayed so you can spot the winner.
          </p>
          {pushCopy && pushCopy.length > 0 ? (
            <div className="space-y-6">
              {pushCopy.map((seg) => (
                <PushCopyRow
                  key={seg.segment}
                  seg={seg}
                  busy={copyBusy}
                  onSave={(variants) => savePushCopy(seg.segment, variants)}
                />
              ))}
            </div>
          ) : (
            <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-4">LOADING PUSH COPY…</div>
          )}
        </div>

        {/* Coupon A/B copy editor */}
        <div className="mt-6 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="coupon-copy-editor">
          <div className="text-[10px] tracking-[0.4em] text-[#39FF14] mb-4 flex items-center gap-2">
            <Percent size={12} /> COUPON BANNER · A/B VARIANTS
          </div>
          <p className="text-xs text-white/50 tracking-wider mb-4">
            Auto-applied coupon banner headline/subtitle. Random variant per view; conversion = clicking checkout with coupon locked in.
          </p>
          {couponCopy && couponCopy.length > 0 ? (
            <div className="space-y-6">
              {couponCopy.map((c) => (
                <CouponCopyRow
                  key={c.code}
                  code={c}
                  busy={couponCopyBusy}
                  onSave={(variants) => saveCouponCopy(c.code, variants)}
                />
              ))}
            </div>
          ) : (
            <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-4">LOADING COUPON COPY…</div>
          )}
        </div>

        <div className="mt-6 text-[10px] tracking-[0.3em] text-white/30 text-center">
          Data source: <span className="text-white/50">payment_transactions</span> · <span className="text-white/50">pro_users</span> · <span className="text-white/50">season_users</span> · <span className="text-white/50">leaderboard</span>
        </div>

        {/* Referral generator */}
        <div className="mt-8 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="referral-panel">
          <div className="text-[10px] tracking-[0.4em] text-[#F0ABFC] mb-3 flex items-center gap-2">
            <Sparkles size={12} /> REFERRAL GENERATOR
          </div>
          <p className="text-xs text-white/50 tracking-wider mb-4">
            Create a shareable <span className="font-mono text-white/70">/r/CODE</span> link — buyers get 15% off, you earn $1 credit per successful sale.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="referral-email-input"
              type="email"
              placeholder="referrer@example.com"
              value={refEmail}
              onChange={(e) => setRefEmail(e.target.value)}
              className="flex-1 min-w-[220px] bg-transparent border-0 border-b border-white/20 rounded-none focus:outline-none focus:border-[#F0ABFC] font-mono text-white py-2 px-1 text-xs"
            />
            <button
              data-testid="referral-create-btn"
              disabled={refBusy || !refEmail}
              onClick={createReferral}
              className="px-4 py-2 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40"
            >
              CREATE LINK
            </button>
          </div>
          {refResult && (
            <div className="mt-4 border border-[#39FF14] bg-[#39FF14]/5 p-3 font-mono text-xs" data-testid="referral-result">
              <div className="text-[10px] tracking-[0.3em] text-[#39FF14] mb-1">SHARE THIS LINK</div>
              <div className="text-white break-all">{window.location.origin}/r/{refResult.code}</div>
              <div className="text-white/50 mt-1">Coupon <span className="text-[#F0ABFC]">{refResult.coupon_id}</span> · {refResult.percent_off}% off</div>
            </div>
          )}
        </div>

        {/* Bulk coupon issuer */}
        <div className="mt-6 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="bulk-coupons-panel">
          <div className="text-[10px] tracking-[0.4em] text-[#FFD400] mb-3 flex items-center gap-2">
            <Upload size={12} /> BULK COUPON ISSUER
          </div>
          <p className="text-xs text-white/50 tracking-wider mb-4">
            Paste emails (one per line, or comma-separated). Each gets a unique single-use Stripe coupon. Perfect for VIP promos.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-[10px] tracking-[0.3em] text-white/50 block mb-1">PREFIX</label>
              <input
                data-testid="bulk-prefix"
                type="text"
                value={bulkPrefix}
                onChange={(e) => setBulkPrefix(e.target.value.toUpperCase().slice(0, 8))}
                className="w-full bg-transparent border-0 border-b border-white/20 rounded-none focus:outline-none focus:border-[#FFD400] font-mono text-white py-2 px-1 text-xs uppercase"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] text-white/50 block mb-1">% OFF</label>
              <input
                data-testid="bulk-percent"
                type="number"
                min={5} max={90}
                value={bulkPercent}
                onChange={(e) => setBulkPercent(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-white/20 rounded-none focus:outline-none focus:border-[#FFD400] font-mono text-white py-2 px-1 text-xs"
              />
            </div>
          </div>
          <textarea
            data-testid="bulk-emails"
            rows={5}
            placeholder="alice@example.com&#10;bob@example.com,charlie@example.com"
            value={bulkCsv}
            onChange={(e) => setBulkCsv(e.target.value)}
            className="w-full bg-transparent border border-white/10 rounded-none focus:outline-none focus:border-[#FFD400] font-mono text-white p-2 text-xs"
          />
          <div className="flex gap-3 mt-3">
            <button
              data-testid="bulk-issue"
              disabled={bulkBusy || !bulkCsv.trim()}
              onClick={issueBulk}
              className="px-4 py-2 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black transition-colors text-[10px] tracking-[0.3em] disabled:opacity-40"
            >
              {bulkBusy ? "ISSUING…" : "ISSUE COUPONS"}
            </button>
            {bulkResult && (
              <button
                data-testid="bulk-download"
                onClick={downloadBulkCsv}
                className="px-4 py-2 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors text-[10px] tracking-[0.3em] flex items-center gap-2"
              >
                <Download size={12} /> DOWNLOAD CSV
              </button>
            )}
            {bulkResult && bulkResult.created > 0 && (
              <button
                data-testid="bulk-dispatch"
                disabled={bulkBusy}
                onClick={async () => {
                  setBulkBusy(true);
                  try {
                    const rows = bulkResult.results.filter(r => r.code);
                    const res = await axios.post(`${API}/admin/coupons/dispatch`, { token, emails: rows });
                    toast.success(`Sent ${res.data.sent} · Failed ${res.data.failed}`);
                  } catch (e) {
                    const detail = e?.response?.data?.detail || "Dispatch failed";
                    toast.error(detail);
                  } finally {
                    setBulkBusy(false);
                  }
                }}
                className="px-4 py-2 border border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors text-[10px] tracking-[0.3em] flex items-center gap-2 disabled:opacity-40"
              >
                <Mail size={12} /> EMAIL VIA RESEND
              </button>
            )}
          </div>
          {bulkResult && (
            <div className="mt-4 border border-white/10 bg-black/60 p-3 max-h-52 overflow-y-auto" data-testid="bulk-result">
              <div className="text-[10px] tracking-[0.3em] text-[#39FF14] mb-2">CREATED {bulkResult.created} · {bulkResult.results.length} PROCESSED</div>
              <table className="w-full text-xs font-mono">
                <tbody className="text-white/80">
                  {bulkResult.results.map((r, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-1">{r.email}</td>
                      <td className="py-1 text-[#F0ABFC]">{r.code || "—"}</td>
                      <td className="py-1 text-right text-[#39FF14]">{r.percent_off ? `${r.percent_off}%` : (r.error || "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sale log */}
        <div className="mt-6 border border-white/10 bg-black/40 backdrop-blur-md p-6" data-testid="tx-log-card">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.4em] text-white/50">SALE LOG · RAW TRANSACTIONS</div>
            <div className="text-[10px] tracking-[0.3em] text-white/40" data-testid="tx-pagination-info">
              {txData ? `PAGE ${txData.page} · ${(txData.page - 1) * txData.per_page + 1}–${Math.min(txData.page * txData.per_page, txData.total)} OF ${txData.total}` : "—"}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="tx-table">
              <thead>
                <tr className="text-[10px] tracking-[0.3em] text-white/40 border-b border-white/10">
                  <th className="text-left py-2">WHEN</th>
                  <th className="text-left">EMAIL</th>
                  <th className="text-left">PRODUCT</th>
                  <th className="text-right">AMOUNT</th>
                  <th className="text-left">COUPON</th>
                  <th className="text-left">STATUS</th>
                </tr>
              </thead>
              <tbody className="font-mono text-white/80">
                {(txData?.rows || []).map((r) => (
                  <tr key={r.session_id} className="border-b border-white/5" data-testid={`tx-row-${r.session_id}`}>
                    <td className="py-2 text-white/60">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="text-white">{r.email || "—"}</td>
                    <td className="text-white/80">{r.lookup_key}</td>
                    <td className="text-right tabular-nums text-[#39FF14]">{fmtUSD(r.amount || 0)}</td>
                    <td className="text-[#F0ABFC]">{r.coupon || "—"}</td>
                    <td className={r.payment_status === "paid" ? "text-[#39FF14]" : "text-white/40"}>{r.payment_status}</td>
                  </tr>
                ))}
                {(!txData || txData.rows.length === 0) && (
                  <tr><td colSpan={6} className="text-center py-4 text-white/30 text-[10px] tracking-[0.3em]">NO TRANSACTIONS YET</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {txData && txData.total > txData.per_page && (
            <div className="flex items-center gap-2 mt-4 justify-end">
              <button
                data-testid="tx-prev"
                disabled={txPage <= 1}
                onClick={() => loadTx(token, txPage - 1)}
                className="px-3 py-1 border border-white/20 text-white/60 hover:border-cyan-300 hover:text-cyan-300 text-[10px] tracking-[0.3em] disabled:opacity-30"
              >
                ← PREV
              </button>
              <button
                data-testid="tx-next"
                disabled={txPage * txData.per_page >= txData.total}
                onClick={() => loadTx(token, txPage + 1)}
                className="px-3 py-1 border border-white/20 text-white/60 hover:border-cyan-300 hover:text-cyan-300 text-[10px] tracking-[0.3em] disabled:opacity-30"
              >
                NEXT →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-white/10 bg-black/40 backdrop-blur-md p-4 flex flex-col gap-1"
      style={{ boxShadow: `inset 0 0 24px ${accent}18` }}
    >
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em]" style={{ color: accent }}>
        {icon} <span>{label}</span>
      </div>
      <div className="font-display text-2xl md:text-3xl tabular-nums text-white mt-1" style={{ textShadow: `0 0 14px ${accent}55` }}>
        {value}
      </div>
      {sub && <div className="text-[10px] tracking-[0.25em] text-white/40">{sub}</div>}
    </div>
  );
}


function PushCopyRow({ seg, busy, onSave }) {
  // Prefill from existing variants or the default text
  const initial = ["A", "B"].map((id) => {
    const v = (seg.variants || []).find((x) => x.id === id);
    if (v) return v;
    return { id, title: seg.default.title, body: seg.default.body, sent: 0, opens: 0, rate: 0 };
  });
  const [rows, setRows] = useState(initial);
  const [previewId, setPreviewId] = useState(null);
  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div className="border border-white/10 p-4" data-testid={`push-copy-${seg.segment}`}>
      <div className="text-[10px] tracking-[0.3em] text-white/70 mb-3">SEGMENT · <span className="text-[#F0ABFC]">{seg.segment.toUpperCase()}</span></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r, i) => {
          const bestRate = Math.max(...rows.map((x) => x.rate || 0));
          const winning = (r.rate || 0) > 0 && r.rate === bestRate;
          return (
            <div key={r.id} className={`border p-3 ${winning ? "border-[#39FF14]" : "border-white/10"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.3em] font-bold" style={{ color: winning ? "#39FF14" : "#F0ABFC" }}>
                  VARIANT {r.id} {winning && <span className="ml-1">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] text-white/50 tabular-nums">{r.opens || 0} / {r.sent || 0} · <span style={{ color: winning ? "#39FF14" : "white" }}>{r.rate || 0}%</span></div>
                  <button
                    data-testid={`push-copy-preview-${seg.segment}-${r.id}`}
                    onClick={() => setPreviewId(previewId === r.id ? null : r.id)}
                    className="text-[9px] tracking-[0.2em] px-2 py-0.5 border border-white/20 text-white/60 hover:text-white/90 hover:border-white/50 transition-colors"
                  >
                    {previewId === r.id ? "HIDE" : "PREVIEW"}
                  </button>
                </div>
              </div>
              <input
                data-testid={`push-copy-title-${seg.segment}-${r.id}`}
                type="text"
                value={r.title}
                onChange={(e) => setRow(i, { title: e.target.value })}
                placeholder="Title (max 60)"
                maxLength={60}
                className="w-full bg-black/60 border border-white/15 text-xs text-white/85 px-2 py-1.5 focus:border-[#F0ABFC] outline-none mb-2 font-mono"
              />
              <textarea
                data-testid={`push-copy-body-${seg.segment}-${r.id}`}
                value={r.body}
                onChange={(e) => setRow(i, { body: e.target.value })}
                placeholder="Body (max 180)"
                maxLength={180}
                rows={2}
                className="w-full bg-black/60 border border-white/15 text-xs text-white/85 px-2 py-1.5 focus:border-[#F0ABFC] outline-none font-mono resize-none"
              />
              {previewId === r.id && (
                <div className="mt-3 border border-white/10 bg-white/5 p-3 flex gap-3" data-testid={`push-copy-mock-${seg.segment}-${r.id}`}>
                  <div className="w-8 h-8 bg-[#111] border border-white/20 flex items-center justify-center text-[10px] text-white/60">🅟</div>
                  <div className="flex-1">
                    <div className="text-xs text-white font-semibold">{r.title || "(no title)"}</div>
                    <div className="text-[11px] text-white/70 leading-snug mt-0.5">{r.body || "(no body)"}</div>
                    <div className="text-[9px] text-white/40 tracking-widest mt-1">Parking Plot Escape · now</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex justify-end">
        <button
          data-testid={`push-copy-save-${seg.segment}`}
          disabled={busy}
          onClick={() => onSave(rows.map(({ id, title, body }) => ({ id, title, body })))}
          className="text-[10px] tracking-[0.3em] px-3 py-1.5 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors disabled:opacity-40"
        >
          SAVE VARIANTS
        </button>
      </div>
    </div>
  );
}

function CouponCopyRow({ code, busy, onSave }) {
  const initial = ["A", "B"].map((id) => {
    const v = (code.variants || []).find((x) => x.id === id);
    if (v) return v;
    return { id, headline: code.default.headline, subtitle: code.default.subtitle, impressions: 0, conversions: 0, rate: 0 };
  });
  const [rows, setRows] = useState(initial);
  const [previewId, setPreviewId] = useState(null);
  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div className="border border-white/10 p-4" data-testid={`coupon-copy-${code.code}`}>
      <div className="text-[10px] tracking-[0.3em] text-white/70 mb-3">CODE · <span className="text-[#39FF14]">{code.code}</span></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r, i) => {
          const bestRate = Math.max(...rows.map((x) => x.rate || 0));
          const winning = (r.rate || 0) > 0 && r.rate === bestRate;
          return (
            <div key={r.id} className={`border p-3 ${winning ? "border-[#39FF14]" : "border-white/10"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.3em] font-bold" style={{ color: winning ? "#39FF14" : "#F0ABFC" }}>
                  VARIANT {r.id} {winning && <span className="ml-1">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] text-white/50 tabular-nums">{r.conversions || 0} / {r.impressions || 0} · <span style={{ color: winning ? "#39FF14" : "white" }}>{r.rate || 0}%</span></div>
                  <button
                    data-testid={`coupon-copy-preview-${code.code}-${r.id}`}
                    onClick={() => setPreviewId(previewId === r.id ? null : r.id)}
                    className="text-[9px] tracking-[0.2em] px-2 py-0.5 border border-white/20 text-white/60 hover:text-white/90 hover:border-white/50 transition-colors"
                  >
                    {previewId === r.id ? "HIDE" : "PREVIEW"}
                  </button>
                </div>
              </div>
              <input
                data-testid={`coupon-copy-headline-${code.code}-${r.id}`}
                type="text"
                value={r.headline}
                onChange={(e) => setRow(i, { headline: e.target.value })}
                placeholder="Headline (max 80)"
                maxLength={80}
                className="w-full bg-black/60 border border-white/15 text-xs text-white/85 px-2 py-1.5 focus:border-[#39FF14] outline-none mb-2 font-mono"
              />
              <textarea
                data-testid={`coupon-copy-subtitle-${code.code}-${r.id}`}
                value={r.subtitle}
                onChange={(e) => setRow(i, { subtitle: e.target.value })}
                placeholder="Subtitle (max 200)"
                maxLength={200}
                rows={2}
                className="w-full bg-black/60 border border-white/15 text-xs text-white/85 px-2 py-1.5 focus:border-[#39FF14] outline-none font-mono resize-none"
              />
              {previewId === r.id && (
                <div className="mt-3 border border-[#39FF14] bg-[#39FF14]/5 px-3 py-2" data-testid={`coupon-copy-mock-${code.code}-${r.id}`}>
                  <div className="text-sm text-[#39FF14] font-bold">{r.headline || "(no headline)"}</div>
                  <div className="text-[11px] text-white/70 mt-1">{r.subtitle || "(no subtitle)"}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex justify-end">
        <button
          data-testid={`coupon-copy-save-${code.code}`}
          disabled={busy}
          onClick={() => onSave(rows.map(({ id, headline, subtitle }) => ({ id, headline, subtitle })))}
          className="text-[10px] tracking-[0.3em] px-3 py-1.5 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors disabled:opacity-40"
        >
          SAVE VARIANTS
        </button>
      </div>
    </div>
  );
}

